import axios from "axios";
import {datadogRum} from "@datadog/browser-rum";
import {useUserStore} from "@/stores/user";
import {debounce} from "lodash";

// Need to assign here, because otherwise we can't access it in the intercom js script
const intercomId = ref('')

// Timestamp of the last successful intercom config load, used to detect an aging identity jwt
let intercomConfigFetchedAt = 0;

// The identity jwt is valid for 1h, we renew it earlier so a long open tab never sends an expired token
const intercomJwtMaxAgeMs = 45 * 60 * 1000;

// Deduplicates concurrent renewals so racing triggers share one refetch instead of stacking requests
let intercomJwtRefreshPromise = null;

/**
 * Inits the intercom system with datadog and etc
 *
 * @param store
 * @returns {Promise<void>}
 */
export async function initIntercom(store) {

    // There is an packaage for that, but we get errors with vite so we need to manually build the stuff here
    // Package: https://github.com/mathieustan/vue-intercom/tree/vue3
    // Error: Can't find composeables

    // Load intercom config
    await store.getIntercomConfig()

    // Without a workspace id there is nothing to boot (config endpoint unavailable)
    if(!intercomId.value) return;

    // Check if intercom is already booted, we need to first shut it down to restart
    // Otherwise we could not set the properties correctly
    if (store.intercomBooted) {
        window.Intercom('shutdown')
    }

    // Load datadog RUM context and generate url to session replay for intercom
    let ddSessionUrl = "DATADOG NOT ENABLED"
    const ddRum = datadogRum.getInternalContext()
    if (ddRum != null) {
        ddSessionUrl = "https://app.datadoghq.eu/rum/sessions?query=@type:session @session.id:" + ddRum.session_id
    }

    // Set initial intercom config values
    let intercomConfig = {
        app_id: intercomId.value,
        background_color: '#009ee3',
        action_color: '#0077c0',
    }

    // If user is logged in aka the app is active, we should add user details and also disable the button
    const userStore = useUserStore()
    const user = userStore.user
    if (user !== null) {
        // Update url to user id based identifying
        ddSessionUrl = "https://app.datadoghq.eu/rum/sessions?query=@type:session @usr.id:" + user.id

        // Identity verification: the backend only returns a jwt for authenticated requests and when the
        // intercom secret is configured. Its payload already asserts user_id, email, name and created_at,
        // so we must not send those as unsigned boot fields anymore. Without a jwt we keep the raw fields
        // as fallback. The token expires after 1h, ensureFreshIntercomJwt() renews it for an already
        // running messenger before it is shown or re-booted.
        const intercomUserJwt = store.intercomConfig?.jwt;

        if(intercomUserJwt) {
            // Add signed user details
            intercomConfig = {
                ...intercomConfig,
                intercom_user_jwt: intercomUserJwt,
            };
        }
        else {
            // Add user details
            intercomConfig = {
                ...intercomConfig,
                email: user.email,
                user_id: user.id,
                name: user.first_name + ' ' + user.last_name,
                created_at: user.created_at,
            };
        }
    }

    // If the user is out of his registration process we should show the intercom app without the floating stuff
    if (user !== null && user.registration_success) {
        // Modify appearence
        intercomConfig = {
            ...intercomConfig,
            hide_default_launcher: true,
            alignment: 'left',
            horizontal_padding: calculateHorizontalPadding()
        }
    }

    // Change position of intercom on resize
    const resizeHandler = debounce(async () => {
        await ensureFreshIntercomJwt(store);

        // This config object is reused on every re-boot, so it has to carry a possibly renewed token
        const refreshedJwt = store.intercomConfig?.jwt;
        if(intercomConfig.intercom_user_jwt && refreshedJwt) {
            intercomConfig.intercom_user_jwt = refreshedJwt;
        }

        intercomConfig.horizontal_padding = calculateHorizontalPadding();
        window.Intercom('boot', intercomConfig);
    }, 250);

    // Event listener for window resize
    if (user) {
        window.addEventListener('resize', resizeHandler);
    }

    // Set dd session url
    intercomConfig = {
        ...intercomConfig,
        'Session Replay' : encodeURI(ddSessionUrl),
    };

    (function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/' + intercomId.value;var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s, x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();
    window.Intercom('boot', intercomConfig);
    window.Intercom('onShow', () => {
        store.intercomVisible = true
        // Intercom can open itself (proactive messages), so the identity is renewed on every show as well
        ensureFreshIntercomJwt(store)
    })
    window.Intercom('onHide', () => {
        store.intercomVisible = false
    })
    store.intercomBooted = true
}

// IntercomConfig horizontal_padding calc
function calculateHorizontalPadding() {
    return window.innerWidth < 1265 ? 80 : 270;
}

/**
 * Renews the identity jwt of an already running messenger before it expires
 *
 * Only does something for logged in users in environments where the backend signs the identity,
 * anonymous visitors and the fallback without a jwt never trigger a refetch.
 *
 * @param {Object} store
 * @returns {Promise<void>}
 */
const ensureFreshIntercomJwt = async (store) => {
    const userStore = useUserStore();

    if(!userStore.user) return;

    if(!store.intercomConfig?.jwt) return;

    if(Date.now() - intercomConfigFetchedAt < intercomJwtMaxAgeMs) return;

    if(!intercomJwtRefreshPromise) {
        intercomJwtRefreshPromise = (async () => {
            try {
                await store.getIntercomConfig();

                // A renewed config can come back without a jwt (secret unset or rotated mid-session),
                // never push an undefined identity into a running messenger
                const renewedJwt = store.intercomConfig?.jwt;
                if(renewedJwt) {
                    window.Intercom('update', { intercom_user_jwt: renewedJwt });
                }
            }
            catch(exception) {
                // Keep the messenger usable with the current token, a failed renewal must not block the user
                console.error('[Intercom] Failed to refresh the identity jwt', exception);
            }
            finally {
                intercomJwtRefreshPromise = null;
            }
        })();
    }

    return intercomJwtRefreshPromise;
};

export async function getIntercomConfig(store) {
    let response;
    try {
        response = await axios.get('api/intercom');
    }
    catch(exception) {
        // A failing config endpoint must never break the app boot, the messenger just stays unavailable
        console.error('[Intercom] Failed to load the messenger config', exception);
        return;
    }

    // An empty body must not wipe a working config or crash the app boot
    if(!response.data?.id) {
        console.warn('[Intercom] Config response had no workspace id, keeping the previous config');
        return;
    }

    store.intercomConfig = response.data;
    intercomId.value = store.intercomConfig.id;
    intercomConfigFetchedAt = Date.now();
}

export async function toggleIntercom(store) {
    // The widget script never loaded when the config endpoint was unavailable
    if(typeof window.Intercom !== 'function') return;

    // Check if we need to open or close it
    if(!store.intercomVisible) {
        await ensureFreshIntercomJwt(store);

        window.Intercom('show');
    }
    else {
        window.Intercom('hide');
    }
}